import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import ImmutablePropTypes from 'react-immutable-proptypes';
import { Map } from 'immutable';
import { Table, TableContainer, TableHead, TableRow, TableCell, TableBody, TextField, Paper } from '@mui/material';
import useMediaQuery from '@mui/material/useMediaQuery';
import variables from 'src/style/variable/variables.module.scss';
import { useTranslation } from 'src/i18n';
import { pairUp } from '../Commissioning/utils';

// These field lists define the order and set of fields rendered in each info table section.
// Each key maps to a translation under commissioningCircuit.table.* and
// commissioningCircuit.controllerConfig.* respectively, and pairs are laid out
// side-by-side via pairUp(). They are hardcoded here because the circuit form
// fields are fixed by product design — they do not vary per facility or device,
// so fetching them from the backend would add complexity with no benefit.
const PROJECT_INFO_FIELDS = [
  'project', 'area', 'cableType', 'ehtTag',
  'numTraces', 'manufacturer', 'controlPanel', 'distributionPanel',
  'controllerHtc', 'circuitBreaker', 'numJb', 'numRtd',
  'ehtIso', 'isoRevision',
];

const CONTROLLER_CONFIG_FIELDS = [
  'address', 'lowTempAlarm', 'controlMode', 'highCurrentAlarm',
  'setpointTemp', 'lowCurrentAlarm', 'groundFaultAlarm', 'groundFaultTripSetpoint',
];


export default function CircuitInfo (props) {
  const { data, onChange, readOnly } = props;
  const { t } = useTranslation();
  const isMobile = useMediaQuery(`(max-width: ${variables.mobileWidth})`);

  const [projectInfo, setProjectInfo] = useState(Map(data?.get('projectInfo') || {}));
  const [controllerConfig, setControllerConfig] = useState(Map(data?.get('controllerConfig') || {}));

  useEffect(() => {
    setProjectInfo(Map(data?.get('projectInfo') || {}));
    setControllerConfig(Map(data?.get('controllerConfig') || {}));
  }, [data]);

  const onProjectChange = (key, val) => {
    const updated = projectInfo.set(key, val);
    setProjectInfo(updated);
    onChange(['extras', 'projectInfo', key], val);
  };

  const onControllerChange = (key, val) => {
    const updated = controllerConfig.set(key, val);
    setControllerConfig(updated);
    onChange(['extras', 'controllerConfig', key], val);
  };

  const projectPairs = pairUp(PROJECT_INFO_FIELDS);
  const controllerPairs = pairUp(CONTROLLER_CONFIG_FIELDS);

  return (
    <div>
      <Paper sx={{ marginBottom: '30px' }}>
        <h3>{t('commissioningCircuit.modalTitle')}</h3>
        <TableContainer className={`default-control-panel-table${isMobile ? ' mobile-table' : ''}`}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className='sub-headers'>{t('common.field')}</TableCell>
                <TableCell className='sub-headers'>{t('common.value')}</TableCell>
                <TableCell className='sub-headers'>{t('common.field')}</TableCell>
                <TableCell className='sub-headers'>{t('common.value')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {projectPairs.map(([fieldA, fieldB]) => (
                <TableRow key={fieldA}>
                  <TableCell className='sub-headers'>{t(`commissioningCircuit.table.${fieldA}`)}</TableCell>
                  <TableCell className='body-cell'>
                    <TextField
                      fullWidth
                      disabled={readOnly}
                      value={projectInfo.get(fieldA) || ''}
                      onChange={(e) => onProjectChange(fieldA, e.target.value)}
                    />
                  </TableCell>
                  {fieldB ? (
                    <>
                      <TableCell className='sub-headers'>{t(`commissioningCircuit.table.${fieldB}`)}</TableCell>
                      <TableCell className='body-cell'>
                        <TextField
                          fullWidth
                          disabled={readOnly}
                          value={projectInfo.get(fieldB) || ''}
                          onChange={(e) => onProjectChange(fieldB, e.target.value)}
                        />
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell />
                      <TableCell />
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Paper sx={{ marginBottom: '30px' }}>
        <h3>{t('commissioningCircuit.controllerConfig.title')}</h3>
        <TableContainer className={`default-control-panel-table${isMobile ? ' mobile-table' : ''}`}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className='sub-headers'>{t('common.field')}</TableCell>
                <TableCell className='sub-headers'>{t('common.value')}</TableCell>
                <TableCell className='sub-headers'>{t('common.field')}</TableCell>
                <TableCell className='sub-headers'>{t('common.value')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {controllerPairs.map(([fieldA, fieldB]) => (
                <TableRow key={fieldA}>
                  <TableCell className='sub-headers'>{t(`commissioningCircuit.controllerConfig.${fieldA}`)}</TableCell>
                  <TableCell className='body-cell'>
                    <TextField
                      fullWidth
                      disabled={readOnly}
                      value={controllerConfig.get(fieldA) || ''}
                      onChange={(e) => onControllerChange(fieldA, e.target.value)}
                    />
                  </TableCell>
                  {fieldB ? (
                    <>
                      <TableCell className='sub-headers'>{t(`commissioningCircuit.controllerConfig.${fieldB}`)}</TableCell>
                      <TableCell className='body-cell'>
                        <TextField
                          fullWidth
                          disabled={readOnly}
                          value={controllerConfig.get(fieldB) || ''}
                          onChange={(e) => onControllerChange(fieldB, e.target.value)}
                        />
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell />
                      <TableCell />
                    </>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </div>
  );
}

CircuitInfo.propTypes = {
  data: ImmutablePropTypes.map,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
};

CircuitInfo.defaultProps = {
  readOnly: false,
};
